import { Crown } from "lucide-react";
import { Panel, StatusBadge } from "@/components/admin/ui";
import { inputClass, labelClass, saveBtnClass } from "./orgFormStyles";

const initials = (name, email) => {
  const base = (name || email || "").trim();
  if (!base) return "?";
  const parts = base.split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return base.slice(0, 2).toUpperCase();
};

export default function OrgOwnerCard({ org, ownerEstablished, ownerInvitationStatus, owner, setOwner, onSendInvite, ownerStatus }) {
  return (
    <Panel
      title="Organization Owner"
      subtitle="The owner can sign in and invite additional organization users, up to the limits below."
      testId="org-owner-card"
    >
      {ownerEstablished ? (
        <div className="rounded-xl border border-slate-200 p-4 dark:border-white/10" data-testid="org-owner-established-card">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#0052FF] to-[#00D4FF] text-sm font-bold text-white">
              {initials(org.owner?.full_name, org.owner?.email)}
            </span>
            <div className="min-w-0">
              <p className="truncate text-sm font-bold text-slate-950 dark:text-white" data-testid="org-owner-name">{org.owner?.full_name}</p>
              <p className="truncate text-sm text-slate-500 dark:text-slate-400" data-testid="org-owner-email">{org.owner?.email}</p>
            </div>
          </div>
          {org.owner?.job_title && (
            <p className="mt-3 text-xs font-semibold text-slate-500 dark:text-slate-400" data-testid="org-owner-job-title-display">{org.owner.job_title}</p>
          )}
          <div
            className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1.5 text-xs font-bold text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400"
            data-testid="org-owner-established-badge"
          >
            <Crown size={13} /> Owner established
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <StatusBadge status={ownerInvitationStatus === "Not Sent" ? "Draft" : ownerInvitationStatus} testId="org-owner-invitation-status" />
          <div>
            <label className={labelClass}>Full Name *</label>
            <input value={owner.full_name} onChange={(e) => setOwner((o) => ({ ...o, full_name: e.target.value }))} data-testid="org-owner-full-name" className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Email *</label>
            <input type="email" value={owner.email} onChange={(e) => setOwner((o) => ({ ...o, email: e.target.value }))} data-testid="org-owner-email" className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Phone</label>
            <input value={owner.phone} onChange={(e) => setOwner((o) => ({ ...o, phone: e.target.value }))} data-testid="org-owner-phone" className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Job Title</label>
            <input value={owner.job_title} onChange={(e) => setOwner((o) => ({ ...o, job_title: e.target.value }))} data-testid="org-owner-job-title" className={inputClass} />
          </div>
          {ownerStatus.error && <p className="rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 dark:bg-red-500/10 dark:text-red-400" data-testid="org-owner-error">{ownerStatus.error}</p>}
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={onSendInvite}
              disabled={ownerStatus.saving || !owner.full_name || !owner.email}
              data-testid="org-owner-send-invite-btn"
              className={saveBtnClass}
            >
              {ownerStatus.saving ? "Sending…" : ownerInvitationStatus === "Not Sent" ? "Send Owner Invitation" : "Resend Owner Invitation"}
            </button>
            {ownerStatus.savedFlash && <span className="text-sm font-semibold text-emerald-600 dark:text-emerald-400" data-testid="org-owner-saved-flash">Invitation sent</span>}
          </div>
        </div>
      )}
    </Panel>
  );
}
